(() => {
  class DropSearch {
    constructor(panel) {
      this.panel = panel;
      this.headerForm = document.querySelector('#header .drop-header-search');
      this.headerInput = this.headerForm?.querySelector('input[type="search"]');
      this.form = panel.querySelector('form.searchform');
      this.input = this.form.querySelector('input[type="search"]');
      this.initial = panel.querySelector('.drop-search__initial');
      this.results = panel.querySelector('.drop-search__results');
      this.status = panel.querySelector('.drop-search__status');
      this.footer = panel.querySelector('.drop-search__footer');
      this.closeButton = panel.querySelector('.drop-search__close');
      this.scrollRegion = panel.querySelector('.drop-search__scroll');
      this.openers = [...document.querySelectorAll('.thb-quick-search')];
      this.media = matchMedia('(min-width: 1200px)');
      this.events = new AbortController();
      this.cache = new Map();
      this.generation = 0;
      this.opened = false;
      this.suppressOpen = false;
      this.setMode();
      this.bind();
    }

    listen(target, name, handler, options = {}) {
      target?.addEventListener(name, handler, { ...options, signal: this.events.signal });
    }

    isDesktop() {
      return this.media.matches && !!this.headerInput;
    }

    setMode() {
      this.panel.dataset.searchMode = this.isDesktop() ? 'popover' : 'modal';
      this.panel.setAttribute('aria-modal', String(!this.isDesktop()));
      if (this.headerInput) {
        this.headerInput.setAttribute('role', 'combobox');
        this.headerInput.setAttribute('aria-autocomplete', 'none');
        this.headerInput.setAttribute('aria-haspopup', 'dialog');
        this.headerInput.setAttribute('aria-controls', this.panel.id);
        this.headerInput.setAttribute('aria-expanded', String(this.opened));
      }
      this.openers.forEach(opener => {
        opener.setAttribute('aria-controls', this.panel.id);
        opener.setAttribute('aria-haspopup', 'dialog');
        opener.setAttribute('aria-expanded', String(this.opened));
      });
    }

    bind() {
      [this.headerInput, this.input].filter(Boolean).forEach(input => {
        this.listen(input, 'input', event => {
          if (!event.isComposing) this.handleInput(input);
        });
        this.listen(input, 'compositionend', () => this.handleInput(input));
      });
      this.listen(this.headerInput, 'pointerdown', event => {
        if (!this.isDesktop() || event.button !== 0 || document.activeElement === this.headerInput) return;
        // Avoid the browser scrolling the header's original flow position into view.
        event.preventDefault();
        this.headerInput.focus({ preventScroll: true });
        this.open(this.headerInput);
      });
      this.listen(this.headerInput, 'focus', () => {
        if (!this.suppressOpen && this.isDesktop()) this.open(this.headerInput);
      });
      this.listen(this.headerInput, 'click', () => {
        if (this.isDesktop()) this.open(this.headerInput);
      });
      [this.headerForm, this.form].filter(Boolean).forEach(form => {
        this.listen(form, 'submit', event => {
          if (!form.querySelector('input[type="search"]').value.trim()) {
            event.preventDefault();
            this.open(form.querySelector('input[type="search"]'));
          }
        });
      });
      this.openers.forEach(opener => this.listen(opener, 'click', event => {
        event.preventDefault();
        if (this.opened) this.close(true);
        else this.open(opener);
      }));
      this.listen(this.closeButton, 'click', () => this.close(true));
      this.listen(document.querySelector('.click-capture'), 'click', () => {
        if (this.opened && !this.isDesktop()) this.close(false);
      });
      this.listen(this.footer.querySelector('button'), 'click', () => {
        (this.isDesktop() ? this.headerForm : this.form).requestSubmit();
      });
      this.listen(document, 'pointerdown', event => {
        if (this.opened && this.isDesktop() && !this.contains(event.target)) this.close(false);
      });
      this.listen(document, 'focusin', event => {
        if (this.opened && this.isDesktop() && !this.contains(event.target)) this.close(false);
      });
      this.listen(document, 'pointerover', event => {
        if (this.opened && this.isDesktop() && event.target.closest?.('.thb-full-menu')) this.close(false);
      });
      this.listen(document, 'keydown', event => this.onKeydown(event));
      this.listen(document, 'panel:open', event => {
        if (event.detail?.panel !== this.panel) this.close(false);
      });
      this.listen(document, 'panel:closed', event => {
        if (event.detail?.panel === this.panel) this.close(false);
      });
      this.listen(this.media, 'change', () => {
        const wasOpen = this.opened;
        this.close(false);
        this.setMode();
        if (wasOpen) {
          this.suppressOpen = true;
          (this.isDesktop() ? this.headerInput : this.openers.find(el => el.getClientRects().length))?.focus({ preventScroll: true });
          this.suppressOpen = false;
        }
      });
      this.listen(window, 'resize', () => this.position());
      this.listen(window, 'scroll', () => {
        if (!this.opened || !this.isDesktop() || this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.position();
        });
      }, { passive: true });
    }

    contains(target) {
      return this.panel.contains(target) || !!this.headerForm?.contains(target);
    }

    position() {
      if (!this.opened || !this.isDesktop()) return;
      const bottom = document.getElementById('header').getBoundingClientRect().bottom;
      if (bottom < 24) {
        this.close(false);
        return;
      }
      this.panel.style.setProperty('--drop-search-top', `${Math.max(12, Math.min(bottom + 10, innerHeight - 160))}px`);
    }

    lockPage() {
      if (this.pageLock) return;
      const body = document.body;
      const scrollbar = Math.max(0, innerWidth - document.documentElement.clientWidth);
      this.pageLock = {
        padding: body.style.getPropertyValue('padding-right'),
        priority: body.style.getPropertyPriority('padding-right'),
        adjusted: scrollbar > 0,
        x: window.scrollX,
        y: window.scrollY
      };
      if (scrollbar > 0) {
        body.style.setProperty('padding-right', `${parseFloat(getComputedStyle(body).paddingRight) + scrollbar}px`);
      }
      body.classList.add('drop-search-open');
      document.documentElement.classList.add('drop-search-open');
    }

    preservePagePosition() {
      if (!this.pageLock) return;
      const { x, y } = this.pageLock;
      // Editing a sticky-header input can scroll its caret into view in Chromium.
      // Restore only while this search owns the page lock.
      const restore = () => {
        if (this.pageLock && (window.scrollX !== x || window.scrollY !== y)) {
          window.scrollTo({ left: x, top: y, behavior: 'instant' });
        }
      };
      restore();
      cancelAnimationFrame(this.scrollRestoreFrame);
      this.scrollRestoreFrame = requestAnimationFrame(restore);
    }

    unlockPage() {
      if (!this.pageLock) return;
      document.body.classList.remove('drop-search-open');
      document.documentElement.classList.remove('drop-search-open');
      if (this.pageLock.adjusted) {
        if (this.pageLock.padding) document.body.style.setProperty('padding-right', this.pageLock.padding, this.pageLock.priority);
        else document.body.style.removeProperty('padding-right');
      }
      cancelAnimationFrame(this.scrollRestoreFrame);
      this.pageLock = null;
    }

    open(source) {
      if (this.opened) return;
      if (this.isDesktop() && typeof OverlayManager !== 'undefined' && OverlayManager.hasOpen()) return;
      this.opener = source || document.activeElement;
      this.opened = true;
      this.setMode();
      if (this.isDesktop()) {
        this.lockPage();
        this.panel.classList.add('active');
        this.panel.inert = false;
        this.position();
        this.headerInput.focus({ preventScroll: true });
      } else if (typeof OverlayManager !== 'undefined') {
        OverlayManager.open(this.panel);
        this.focusTimer = setTimeout(() => {
          if (this.opened && !this.isDesktop()) this.input.focus({ preventScroll: true });
        }, 100);
      } else {
        this.opened = false;
        location.assign(this.form.action);
        return;
      }
      this.update(source === this.headerInput ? this.headerInput : this.isDesktop() ? this.headerInput : this.input);
      document.dispatchEvent(new CustomEvent('search:open'));
    }

    close(restore) {
      if (!this.opened) return;
      this.suppressOpen = true;
      clearTimeout(this.focusTimer);
      this.opened = false;
      this.cancelRequest();
      if (typeof OverlayManager !== 'undefined' && OverlayManager.isOpen(this.panel)) {
        OverlayManager.close(this.panel);
      }
      this.panel.classList.remove('active');
      this.panel.inert = true;
      this.unlockPage();
      this.setMode();
      if (restore) {
        const target = this.opener?.getClientRects().length ? this.opener : this.isDesktop() ? this.headerInput : this.openers.find(el => el.getClientRects().length);
        target?.focus({ preventScroll: true });
      }
      this.suppressOpen = false;
    }

    handleInput(input) {
      if (!this.opened) this.open(input);
      else this.update(input);
    }

    cancelRequest() {
      this.generation += 1;
      clearTimeout(this.timer);
      clearTimeout(this.timeout);
      this.request?.abort();
      this.panel.setAttribute('aria-busy', 'false');
    }

    update(input) {
      this.preservePagePosition();
      const value = input.value;
      if (this.headerInput) this.headerInput.value = value;
      this.input.value = value;
      this.query = value.trim();
      this.cancelRequest();
      const generation = this.generation;
      const predictive = this.panel.dataset.predictive === 'true';
      this.footer.hidden = !this.query;
      this.initial.hidden = !!this.query && predictive;
      this.results.hidden = !this.query || !predictive;
      this.results.replaceChildren();
      if (this.scrollRegion) this.scrollRegion.scrollTop = 0;
      this.status.textContent = '';
      if (!this.query || !predictive) return;
      if (this.cache.has(this.query)) {
        this.render(this.cache.get(this.query));
        return;
      }
      this.status.textContent = this.panel.dataset.loading;
      this.panel.setAttribute('aria-busy', 'true');
      const query = this.query;
      this.timer = setTimeout(() => this.fetchResults(query, generation), 250);
    }

    async fetchResults(query, generation) {
      if (generation !== this.generation || !this.opened) return;
      this.request = new AbortController();
      const request = this.request;
      this.timeout = setTimeout(() => request.abort(), 12000);
      try {
        const route = window.theme?.routes?.predictive_search_url;
        if (!route) throw new Error('Missing predictive search route');
        const params = new URLSearchParams({
          q: query,
          'resources[type]': 'product,article,query',
          'resources[limit]': '8',
          'resources[options][fields]': 'title,product_type,vendor,variants.title,variants.sku',
          section_id: 'predictive-search'
        });
        const response = await fetch(`${route}?${params}`, { signal: request.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error(String(response.status));
        const text = await response.text();
        if (generation !== this.generation || !this.opened || query !== this.query) return;
        const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        if (!section) throw new Error('Missing predictive search section');
        if (this.cache.size >= 30) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(query, section.innerHTML);
        this.render(section.innerHTML);
      } catch {
        if (generation === this.generation && this.opened) {
          this.results.replaceChildren();
          this.status.textContent = this.panel.dataset.error;
        }
      } finally {
        if (generation === this.generation) {
          clearTimeout(this.timeout);
          this.panel.setAttribute('aria-busy', 'false');
        }
      }
    }

    render(html) {
      this.results.innerHTML = html;
      this.status.textContent = this.results.querySelector('[data-search-count]')?.textContent || '';
      this.panel.setAttribute('aria-busy', 'false');
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.opened) {
        event.preventDefault();
        this.close(true);
        return;
      }
      if (!this.isDesktop()) return;
      if (event.target === this.headerInput && event.key === 'ArrowDown') {
        event.preventDefault();
        this.open(this.headerInput);
        (this.panel.querySelector('.drop-search__grid a') || this.closeButton).focus({ preventScroll: true });
      }
      if (!this.opened || event.key !== 'Tab') return;
      if (event.target === this.headerInput && !event.shiftKey) {
        event.preventDefault();
        this.closeButton.focus({ preventScroll: true });
      } else if (event.target === this.closeButton && event.shiftKey) {
        event.preventDefault();
        this.headerInput.focus({ preventScroll: true });
      }
    }

    destroy() {
      this.close(false);
      this.cancelRequest();
      this.events.abort();
      if (this.frame) cancelAnimationFrame(this.frame);
      clearTimeout(this.focusTimer);
    }
  }

  window.dropSearchBootEvents?.abort();
  window.dropSearchBootEvents = new AbortController();
  const boot = () => {
    window.dropSearchController?.destroy();
    const panel = document.getElementById('Search-Drawer');
    window.dropSearchController = panel?.querySelector('.drop-search__results') ? new DropSearch(panel) : null;
  };
  document.addEventListener('shopify:section:load', boot, { signal: window.dropSearchBootEvents.signal });
  document.addEventListener('shopify:section:unload', () => window.dropSearchController?.close(false), { signal: window.dropSearchBootEvents.signal });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true, signal: window.dropSearchBootEvents.signal });
  } else {
    boot();
  }
})();
